import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../../components/seo/SEOHead';
import { Input, Select } from '../../../components/ui/Input';
import { DataTable } from '../../../components/admin/DataTable';
import { ConsultationStatusBadge } from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Card';
import { api } from '../../../lib/api';
import {
  CONSULTATION_STATUSES,
  CONSULTATION_STATUS_LABELS,
  type ConsultationBooking,
  type ConsultationStatus,
} from '@bharat-consultancy/shared';

export default function ConsultationList() {
  const [bookings, setBookings] = useState<ConsultationBooking[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConsultationStatus | ''>('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    const timeout = setTimeout(() => {
      api.admin.consultations
        .list({ search, status: statusFilter || undefined })
        .then((res) => setBookings(res.items))
        .catch(() => setBookings([]))
        .finally(() => setIsLoading(false));
    }, 300);
    return () => clearTimeout(timeout);
  }, [search, statusFilter]);

  return (
    <>
      <SEOHead title="Consultations — Admin" description="" canonicalPath="/admin/consultations" />

      <h1 className="font-display text-2xl font-bold text-ink">Consultation Bookings</h1>
      <p className="mt-1 text-sm text-ink/60">
        Visitors who paid for a 30-minute call before committing to a service. Call them at the requested time and
        log the outcome below.
      </p>

      <div className="mt-4 flex flex-wrap gap-3">
        <Input
          placeholder="Search by name, email, mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ConsultationStatus | '')}
          className="max-w-xs"
        >
          <option value="">All Statuses</option>
          {CONSULTATION_STATUSES.map((status) => (
            <option key={status} value={status}>
              {CONSULTATION_STATUS_LABELS[status]}
            </option>
          ))}
        </Select>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <Spinner className="h-6 w-6 text-seal-blue" />
        ) : (
          <DataTable
            columns={[
              { header: 'Name', render: (b) => b.name },
              { header: 'Contact', render: (b) => <span className="text-xs">{b.mobile} · {b.email}</span> },
              { header: 'Service', render: (b) => b.service?.title ?? '—' },
              {
                header: 'Preferred Time',
                render: (b) => (b.preferredDate ? `${b.preferredDate} ${b.preferredTime ?? ''}` : '—'),
              },
              { header: 'Amount', render: (b) => `₹${b.amount.toLocaleString('en-IN')}` },
              { header: 'Status', render: (b) => <ConsultationStatusBadge status={b.status} /> },
              { header: 'Booked On', render: (b) => new Date(b.createdAt).toLocaleDateString('en-IN') },
            ]}
            rows={bookings}
            keyExtractor={(b) => b.id}
            onRowClick={(b) => navigate(`/admin/consultations/${b.id}`)}
            emptyMessage="No consultation bookings yet."
          />
        )}
      </div>
    </>
  );
}
